import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EmojiObjects as EmojiObjectsIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { hostname } from "../../../utils";

interface Memory {
  id: string;
  content: string;
  category: string | null;
  isActive: boolean;
  companyId: string | null;
  createdDate: string;
  updatedDate: string | null;
}

interface MemoriesDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
}

const CATEGORIES = [
  { value: "", label: "All Categories" },
  { value: "preference", label: "Preferences" },
  { value: "fact", label: "Facts" },
  { value: "instruction", label: "Instructions" },
  { value: "context", label: "Context" },
];

const getCategoryColor = (category: string | null) => {
  switch (category) {
    case "preference":
      return "primary";
    case "fact":
      return "info";
    case "instruction":
      return "warning";
    case "context":
      return "secondary";
    default:
      return "default";
  }
};

const getCategoryLabel = (category: string | null) => {
  const cat = CATEGORIES.find((c) => c.value === category);
  return cat ? cat.label.replace(/s$/, "") : "Memory";
};

export default function MemoriesDialog({
  open,
  onClose,
  companyId,
  getToken,
}: MemoriesDialogProps) {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [editingMemory, setEditingMemory] = useState<Memory | null>(null);
  const [editContent, setEditContent] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState("");
  const [newMemoryCategory, setNewMemoryCategory] = useState("");
  const [newMemoryScope, setNewMemoryScope] = useState<"global" | "company">("global");
  const [addingMemory, setAddingMemory] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [deleteConfirmMemory, setDeleteConfirmMemory] = useState<Memory | null>(null);

  const fetchMemories = useCallback(async () => {
    if (!open || !companyId) return;

    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: "20",
        includeInactive: showInactive.toString(),
      });
      if (category) {
        params.append("category", category);
      }

      const response = await axios.get(
        `${hostname}/companies/${companyId}/memories?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setMemories(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching memories:", error);
      setSnackbar({
        open: true,
        message: "Failed to load memories",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [open, companyId, getToken, page, category, showInactive]);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  const handleToggleActive = async (memory: Memory) => {
    try {
      const token = await getToken();
      await axios.patch(
        `${hostname}/companies/${companyId}/memories/${memory.id}/toggle`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSnackbar({
        open: true,
        message: memory.isActive ? "Memory disabled" : "Memory enabled",
        severity: "success",
      });
      fetchMemories();
    } catch (error) {
      console.error("Error toggling memory:", error);
      setSnackbar({
        open: true,
        message: "Failed to toggle memory",
        severity: "error",
      });
    }
  };

  const handleDelete = async (memory: Memory) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/companies/${companyId}/memories/${memory.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSnackbar({
        open: true,
        message: "Memory deleted",
        severity: "success",
      });
      fetchMemories();
    } catch (error) {
      console.error("Error deleting memory:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete memory",
        severity: "error",
      });
    } finally {
      setDeleteConfirmMemory(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingMemory || !editContent.trim()) return;

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/companies/${companyId}/memories/${editingMemory.id}`,
        { content: editContent.trim() },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSnackbar({
        open: true,
        message: "Memory updated",
        severity: "success",
      });
      setEditingMemory(null);
      setEditContent("");
      fetchMemories();
    } catch (error) {
      console.error("Error updating memory:", error);
      setSnackbar({
        open: true,
        message: "Failed to update memory",
        severity: "error",
      });
    }
  };

  const handleAddMemory = async () => {
    if (!newMemoryContent.trim()) return;

    setAddingMemory(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/companies/${companyId}/memories`,
        {
          content: newMemoryContent.trim(),
          category: newMemoryCategory || null,
          // companyId is null for global scope, or companyId for company-scoped
          companyId: newMemoryScope === "company" ? companyId : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSnackbar({
        open: true,
        message: "Memory added",
        severity: "success",
      });
      setShowAddForm(false);
      setNewMemoryContent("");
      setNewMemoryCategory("");
      setNewMemoryScope("global");
      fetchMemories();
    } catch (error) {
      console.error("Error adding memory:", error);
      setSnackbar({
        open: true,
        message: "Failed to add memory",
        severity: "error",
      });
    } finally {
      setAddingMemory(false);
    }
  };

  const handleToggleScope = async (memory: Memory) => {
    try {
      const token = await getToken();
      // Toggle between global (null) and company-scoped
      const newCompanyId = memory.companyId === null ? companyId : null;
      await axios.put(
        `${hostname}/companies/${companyId}/memories/${memory.id}`,
        { companyId: newCompanyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSnackbar({
        open: true,
        message: memory.companyId === null
          ? "Memory restricted to this company only"
          : "Memory now used in all your companies",
        severity: "success",
      });
      fetchMemories();
    } catch (error) {
      console.error("Error toggling memory scope:", error);
      setSnackbar({
        open: true,
        message: "Failed to update memory scope",
        severity: "error",
      });
    }
  };

  const filteredMemories = search
    ? memories.filter((m) =>
        m.content.toLowerCase().includes(search.toLowerCase())
      )
    : memories;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmojiObjectsIcon color="primary" />
              <Typography variant="h6">Memories</Typography>
              <Chip
                label="Beta"
                size="small"
                sx={{
                  backgroundColor: "grey.500",
                  color: "white",
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: 550, height: 550 }}>
          {/* Description */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These are facts and preferences the AI has learned about you from your conversations.
            Memories help personalize responses across your chats.
          </Typography>

          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              placeholder="Search memories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <Select
              size="small"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
              displayEmpty
              sx={{ minWidth: 150 }}
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
            <Button
              size="small"
              variant={showInactive ? "contained" : "outlined"}
              onClick={() => {
                setShowInactive(!showInactive);
                setPage(1);
              }}
            >
              {showInactive ? "Showing All" : "Show Inactive"}
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddForm(true)}
            >
              Add Memory
            </Button>
          </Box>

          {/* Add Memory Form */}
          {showAddForm && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add New Memory
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter a fact, preference, or instruction..."
                value={newMemoryContent}
                onChange={(e) => setNewMemoryContent(e.target.value)}
                multiline
                minRows={2}
                sx={{ mb: 1 }}
                autoFocus
              />
              <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <Select
                  size="small"
                  value={newMemoryCategory}
                  onChange={(e) => setNewMemoryCategory(e.target.value)}
                  displayEmpty
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="">No Category</MenuItem>
                  <MenuItem value="preference">Preference</MenuItem>
                  <MenuItem value="fact">Fact</MenuItem>
                  <MenuItem value="instruction">Instruction</MenuItem>
                  <MenuItem value="context">Context</MenuItem>
                </Select>
                <Select
                  size="small"
                  value={newMemoryScope}
                  onChange={(e) => setNewMemoryScope(e.target.value as "global" | "company")}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="global">All Companies</MenuItem>
                  <MenuItem value="company">This Company Only</MenuItem>
                </Select>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setShowAddForm(false);
                    setNewMemoryContent("");
                    setNewMemoryCategory("");
                    setNewMemoryScope("global");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddMemory}
                  disabled={!newMemoryContent.trim() || addingMemory}
                >
                  {addingMemory ? "Adding..." : "Add"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Memory List */}
          <Box
            sx={{
              height: 400,
              overflow: "auto",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredMemories.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <EmojiObjectsIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">No memories found</Typography>
                <Typography variant="body2" color="text.secondary">
                  Memories are saved when you share facts, preferences, or
                  instructions during conversations.
                </Typography>
              </Box>
            ) : (
              <List>
                {filteredMemories.map((memory) => (
                <ListItem
                  key={memory.id}
                  sx={{
                    bgcolor: memory.isActive ? "transparent" : "action.hover",
                    opacity: memory.isActive ? 1 : 0.6,
                    borderRadius: 1,
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    flexDirection: editingMemory?.id === memory.id ? "column" : "row",
                    alignItems: editingMemory?.id === memory.id ? "stretch" : "center",
                  }}
                >
                  {editingMemory?.id === memory.id ? (
                    /* Edit mode layout */
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: memory.isActive ? "primary.main" : "action.disabled",
                            width: 32,
                            height: 32,
                          }}
                        >
                          <EmojiObjectsIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          Editing memory
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit();
                          if (e.key === "Escape") {
                            setEditingMemory(null);
                            setEditContent("");
                          }
                        }}
                        autoFocus
                        multiline
                        minRows={2}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingMemory(null);
                            setEditContent("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveEdit}
                        >
                          Save
                        </Button>
                      </Box>
                    </>
                  ) : (
                    /* View mode layout */
                    <>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: memory.isActive ? "primary.main" : "action.disabled",
                          }}
                        >
                          <EmojiObjectsIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2">{memory.content}</Typography>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            <Tooltip
                              title={
                                memory.companyId === null
                                  ? "All Companies - this memory is used in all your companies"
                                  : "This Company Only - this memory is only used in this specific company"
                              }
                            >
                              <Chip
                                icon={memory.companyId === null ? <PersonIcon /> : <BusinessIcon />}
                                label={memory.companyId === null ? "All Companies" : "This Company Only"}
                                size="small"
                                color={memory.companyId === null ? "primary" : "default"}
                                variant="outlined"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                            </Tooltip>
                            {memory.category && (
                              <Chip
                                label={getCategoryLabel(memory.category)}
                                size="small"
                                color={getCategoryColor(memory.category) as any}
                                variant="outlined"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {new Date(memory.createdDate).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip
                          title={
                            memory.companyId === null
                              ? "Restrict to this company only"
                              : "Use in all your companies"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleToggleScope(memory)}
                          >
                            {memory.companyId === null ? (
                              <BusinessIcon fontSize="small" />
                            ) : (
                              <PersonIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={memory.isActive ? "Disable" : "Enable"}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleActive(memory)}
                          >
                            {memory.isActive ? (
                              <ToggleOnIcon color="primary" />
                            ) : (
                              <ToggleOffIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingMemory(memory);
                              setEditContent(memory.content);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirmMemory(memory)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
              </List>
            )}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmMemory !== null}
        onClose={() => setDeleteConfirmMemory(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Memory</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this memory?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmMemory(null)}>Cancel</Button>
          <Button
            onClick={() => deleteConfirmMemory && handleDelete(deleteConfirmMemory)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
